import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import FormField from '@/components/FormField';
import PackageStepIndicator from '@/components/PackageStepIndicator';
import { getPackage, getStepPath, getLevelStyles } from '@/data/packageData';
import { usePackage } from '@/context/PackageContext';

const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

const REFERRAL_OPTIONS = [
  'Word-of-Mouth',
  'Our Website',
  "Xero's Website (List of Xero Partners)",
  'Google Search',
  'Brochure Handout',
  'Board Sign',
  'Advert Sign',
  'LinkedIn',
  'Facebook'
];

const BUSINESS_STRUCTURES = [
  'Sole Proprietor',
  'Partnership',
  'Personal Liability Company (Inc.)',
  'Close Corporation (CC)',
  'Private Company (Pty) Ltd',
  'Public Company (Ltd)',
  'Non-Profit Company (NPC)',
  'Trust',
];

// Comprehensive South African bank list, branch codes, and account-number
// validation rules (sourced from the client's master list).
const BANKS = [
  'ABSA Bank',
  'African Bank',
  'Albaraka Bank',
  'Bank Zero',
  'Barclays Bank',
  'Bidvest Bank',
  'Capitec Bank',
  'Capitec Business Bank',
  'Discovery Bank',
  'First National Bank (FNB)',
  'FirstRand Bank Ltd',
  'Grindrod Bank',
  'Habib Overseas Bank',
  'HBZ Bank',
  'Hollard Bank',
  'HSBC Bank',
  'Investec Bank',
  'Ithala Bank',
  'Mercantile Bank (now Capitec Business Bank)',
  'Nedbank',
  'Old Mutual Bank',
  'PostBank (South African Post Office)',
  'Rand Merchant Bank',
  'RMB Private Bank',
  'Sasfin Bank',
  'South African Bank of Athens Limited',
  'Standard Bank',
  'Standard Chartered Bank',
  'TymeBank',
  'Ubank',
];

const BANK_BRANCH_CODES = {
  'ABSA Bank': '632005',
  'African Bank': '430000',
  'Albaraka Bank': '800000',
  'Bank Zero': '888000',
  'Barclays Bank': '590000',
  'Bidvest Bank': '462005',
  'Capitec Bank': '470010',
  'Capitec Business Bank': '450105',
  'Discovery Bank': '679000',
  'First National Bank (FNB)': '250655',
  'FirstRand Bank Ltd': '201419',
  'Grindrod Bank': '584000',
  'Habib Overseas Bank': '170102',
  'HBZ Bank': '570105',
  'Hollard Bank': '585001',
  'HSBC Bank': '587000',
  'Investec Bank': '580105',
  'Ithala Bank': '420000',
  'Mercantile Bank (now Capitec Business Bank)': '450105',
  'Nedbank': '198765',
  'Old Mutual Bank': '462005',
  'PostBank (South African Post Office)': '460005',
  'Rand Merchant Bank': '261251',
  'RMB Private Bank': '222026',
  'Sasfin Bank': '683000',
  'South African Bank of Athens Limited': '410506',
  'Standard Bank': '051001',
  'Standard Chartered Bank': '730020',
  'TymeBank': '678910',
  'Ubank': '431010',
};

// Per-bank account number validation rules
const BANK_ACCOUNT_RULES = {
  'ABSA Bank':                                   { minLength: 10, maxLength: 11, prefixes: ['1','4','9'],     prefixRequired: false, description: '10 or 11 digits',                                  note: 'Accounts are left-padded with zeros to 11 digits for CDV processing.' },
  'African Bank':                                { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'Albaraka Bank':                               { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits',                                        note: 'Islamic Banking. Do not enter international IBAN format.' },
  'Bank Zero':                                   { minLength: 10, maxLength: 11, prefixes: null,              prefixRequired: false, description: '10 or 11 digits' },
  'Barclays Bank':                               { minLength: 10, maxLength: 11, prefixes: null,              prefixRequired: false, description: '10 or 11 digits' },
  'Bidvest Bank':                                { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'Capitec Bank':                                { minLength: 10, maxLength: 10, prefixes: ['1','2'],         prefixRequired: true,  description: '10 digits',                                        note: 'Savings accounts start with 1, Loan/Bond accounts start with 2.' },
  'Capitec Business Bank':                       { minLength: 10, maxLength: 10, prefixes: ['1','2'],         prefixRequired: false, description: '10 digits' },
  'Discovery Bank':                              { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits',                                        rejectLength: 16, note: 'Please enter your account number, not your 16-digit card number.' },
  'First National Bank (FNB)':                   { minLength: 11, maxLength: 11, prefixes: ['5','6','7'],     prefixRequired: true,  description: '11 digits',                                        note: 'Cheque accounts start with 6, Savings with 7 or 5.' },
  'FirstRand Bank Ltd':                          { minLength: 11, maxLength: 11, prefixes: ['5','6','7'],     prefixRequired: false, description: '11 digits',                                        note: 'Parent company of FNB - uses same validation rules.' },
  'Grindrod Bank':                               { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'Habib Overseas Bank':                         { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'HBZ Bank':                                    { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'Hollard Bank':                                { minLength: 10, maxLength: 11, prefixes: null,              prefixRequired: false, description: '10 or 11 digits' },
  'HSBC Bank':                                   { minLength: 10, maxLength: 11, prefixes: null,              prefixRequired: false, description: '10 or 11 digits' },
  'Investec Bank':                               { minLength: 11, maxLength: 11, prefixes: ['1','5'],         prefixRequired: false, description: '11 digits',                                        note: 'Private Bank accounts often start with 1, Corporate with 5.' },
  'Ithala Bank':                                 { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'Mercantile Bank (now Capitec Business Bank)': { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'Nedbank':                                     { minLength: 10, maxLength: 10, prefixes: ['1'],             prefixRequired: true,  description: '10 digits',                                        note: 'Please ensure you enter your Account Number, not your Profile Number.' },
  'Old Mutual Bank':                             { minLength: 10, maxLength: 11, prefixes: null,              prefixRequired: false, description: '10 or 11 digits' },
  'PostBank (South African Post Office)':        { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'Rand Merchant Bank':                          { minLength: 11, maxLength: 11, prefixes: ['5','6','7'],     prefixRequired: false, description: '11 digits',                                        note: 'Uses same validation rules as FNB.' },
  'RMB Private Bank':                            { minLength: 11, maxLength: 11, prefixes: ['5','6','7'],     prefixRequired: false, description: '11 digits',                                        note: 'Uses same validation rules as FNB.' },
  'Sasfin Bank':                                 { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'South African Bank of Athens Limited':        { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits',                                        note: 'Formerly Access Bank South Africa.' },
  'Standard Bank':                               { minLength: 9,  maxLength: 11, prefixes: ['0','1','2','3','4'], prefixRequired: false, description: '9 to 11 digits',                              note: '9-digit accounts are valid and will be left-padded with zeros for processing.' },
  'Standard Chartered Bank':                     { minLength: 10, maxLength: 11, prefixes: null,              prefixRequired: false, description: '10 or 11 digits' },
  'TymeBank':                                    { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits' },
  'Ubank':                                       { minLength: 11, maxLength: 11, prefixes: null,              prefixRequired: false, description: '11 digits',                                        note: 'Formerly Teba Bank.' },
};

// Generic fallback when no bank is selected
const GENERIC_ACCOUNT_RULE = { minLength: 9, maxLength: 11, prefixes: null, prefixRequired: false, description: '9 to 11 digits' };

const STRUCTURE_FIELD_LABELS = {
  'Sole Proprietor':                  { name: 'Business Name',     reg: 'Home Affairs ID #',          namePlaceholder: 'Enter your trading/business name',    regPlaceholder: 'Enter your 13-digit SA ID number' },
  'Partnership':                      { name: 'Partnership Name',  reg: 'Home Affairs ID #',          namePlaceholder: 'Enter the partnership name',           regPlaceholder: 'Enter your 13-digit SA ID number' },
  'Personal Liability Company (Inc.)':{ name: 'Company Name',      reg: 'Company Registration #',     namePlaceholder: 'Enter registered company name',       regPlaceholder: 'e.g. 2023/123456/21' },
  'Close Corporation (CC)':           { name: 'Company Name',      reg: 'Company Registration #',     namePlaceholder: 'Enter registered company name',       regPlaceholder: 'e.g. 2023/123456/23' },
  'Private Company (Pty) Ltd':        { name: 'Company Name',      reg: 'Company Registration #',     namePlaceholder: 'Enter registered company name',       regPlaceholder: 'e.g. 2023/123456/07' },
  'Public Company (Ltd)':             { name: 'Company Name',      reg: 'Company Registration #',     namePlaceholder: 'Enter registered company name',       regPlaceholder: 'e.g. 2023/123456/06' },
  'Non-Profit Company (NPC)':         { name: 'Company Name',      reg: 'Company Registration #',     namePlaceholder: 'Enter registered NPC name',           regPlaceholder: 'e.g. 2023/123456/08' },
  'Trust':                            { name: 'Trust Name',        reg: 'Trust Registration #',       namePlaceholder: 'Enter the trust name',                regPlaceholder: 'e.g. IT123/2020' },
};

// CIPC suffix per company-type structure (last 2 digits of YYYY/NNNNNN/EE)
const COMPANY_REG_SUFFIX = {
  'Personal Liability Company (Inc.)': '21',
  'Close Corporation (CC)':            '23',
  'Private Company (Pty) Ltd':         '07',
  'Public Company (Ltd)':              '06',
  'Non-Profit Company (NPC)':          '08',
};

const getStructureLabels = (structure) =>
  STRUCTURE_FIELD_LABELS[structure] || { name: 'Company Name', reg: 'Company Registration #', namePlaceholder: 'Enter registered company name', regPlaceholder: 'e.g. 2023/123456/07' };

const PersonalDetails = () => {
  const { packageSlug } = useParams();
  const navigate = useNavigate();
  const pkg = getPackage(packageSlug);
  const { updateClientData, clientData } = usePackage();

  const [clientType, setClientType] = useState(clientData?.clientType || '');
  const [formData, setFormData] = useState({
    businessStructure: clientData?.businessStructure || '',
    companyName: clientData?.companyName || '',
    companyRegistration: clientData?.companyRegistration || '',
    firstName: clientData?.firstName || '',
    secondName: clientData?.secondName || '',
    middleName: clientData?.middleName || '',
    surname: clientData?.surname || '',
    idNumber: clientData?.idNumber || '',
    taxNumber: clientData?.taxNumber || '',
    vatNumber: clientData?.vatNumber || '',
    emailAddress: clientData?.emailAddress || '',
    workEmail: clientData?.workEmail || '',
    cellphoneNumber: clientData?.cellphoneNumber || '',
    physicalAddress: clientData?.physicalAddress || '',
    bankName: clientData?.bankName || '',
    branchCode: clientData?.branchCode || '',
    accountOwner: clientData?.accountOwner || '',
    accountNumber: clientData?.accountNumber || '',
    referralSource: clientData?.referralSource || ''
  });
  // Tracks whether the user manually edited the Account Owner; used to know
  // whether to keep auto-syncing it from Company Name.
  const [accountOwnerEdited, setAccountOwnerEdited] = useState(!!clientData?.accountOwner);
  const [errorModal, setErrorModal] = useState({ open: false, message: '' });
  const [fieldErrors, setFieldErrors] = useState({
    emailAddress: '',
    workEmail: '',
    idNumber: '',
    taxNumber: '',
    vatNumber: '',
    cellphoneNumber: '',
    companyRegistration: '',
    companyName: '',
    bankName: '',
    accountOwner: '',
    accountNumber: ''
  });
  const [touchedFields, setTouchedFields] = useState({
    emailAddress: false,
    workEmail: false,
    idNumber: false,
    taxNumber: false,
    vatNumber: false,
    cellphoneNumber: false,
    companyRegistration: false,
    companyName: false,
    bankName: false,
    accountOwner: false,
    accountNumber: false
  });
  const [idNumberInfo, setIdNumberInfo] = useState(null);
  const [companyRegIdInfo, setCompanyRegIdInfo] = useState(null);
  const [showAddressTip, setShowAddressTip] = useState(false);

  // Structures whose registration field is actually a SA ID number
  const idLikeStructures = ['Sole Proprietor', 'Partnership'];
  const isIdLikeStructure = idLikeStructures.includes(formData.businessStructure);
  const isTrustStructure = formData.businessStructure === 'Trust';
  const isCompanyRegStructure = !!COMPANY_REG_SUFFIX[formData.businessStructure];
  // Structures that have a CIPC-mandated company-name suffix
  const nameValidatedStructures = [
    'Private Company (Pty) Ltd',
    'Public Company (Ltd)',
    'Non-Profit Company (NPC)',
    'Close Corporation (CC)',
    'Personal Liability Company (Inc.)',
  ];
  const isNameValidatedStructure = nameValidatedStructures.includes(formData.businessStructure);

  // Validates company name suffix per CIPC rules (no validation for Sole Proprietor / Partnership / Trust)
  const validateCompanyName = (structure, rawValue) => {
    const value = (rawValue || '').trim();
    if (value === '') return { valid: false, error: '' };
    switch (structure) {
      case 'Private Company (Pty) Ltd':
        if (!/\(pty\)\s+ltd$/i.test(value)) return { valid: false, error: 'Private Company names must end with "(Pty) Ltd"' };
        return { valid: true, error: '' };
      case 'Public Company (Ltd)':
        if (/\(pty\)\s+ltd$/i.test(value)) return { valid: false, error: 'Public Company names must end with "Ltd" only (without "(Pty)")' };
        if (!/\bltd$/i.test(value)) return { valid: false, error: 'Public Company names must end with "Ltd"' };
        return { valid: true, error: '' };
      case 'Non-Profit Company (NPC)':
        if (!/\bnpc$/i.test(value)) return { valid: false, error: 'Non-Profit Company names must end with "NPC"' };
        return { valid: true, error: '' };
      case 'Close Corporation (CC)':
        if (!/\bcc$/i.test(value)) return { valid: false, error: 'Close Corporation names must end with "CC"' };
        return { valid: true, error: '' };
      case 'Personal Liability Company (Inc.)':
        if (!/\bincorporated$/i.test(value)) return { valid: false, error: 'Personal Liability Company names must end with "Incorporated"' };
        return { valid: true, error: '' };
      default:
        return { valid: true, error: '' };
    }
  };

  // Validates a bank account number against the selected bank's rule
  const validateBankAccountNumber = (bankName, rawValue) => {
    const value = (rawValue || '').replace(/\D/g, '');
    if (value === '') return { valid: false, error: '' };
    const rule = BANK_ACCOUNT_RULES[bankName] || GENERIC_ACCOUNT_RULE;
    if (rule.rejectLength && value.length === rule.rejectLength) {
      return { valid: false, error: rule.note || `Please enter your account number, not your ${rule.rejectLength}-digit card number.` };
    }
    if (value.length < rule.minLength) {
      return { valid: false, error: `${bankName || 'Bank'} account numbers must be ${rule.description}. Currently ${value.length} digits.` };
    }
    if (value.length > rule.maxLength) {
      return { valid: false, error: `${bankName || 'Bank'} account numbers must be ${rule.description}. Currently ${value.length} digits.` };
    }
    if (rule.prefixRequired && rule.prefixes && rule.prefixes.length) {
      const firstChar = value[0];
      if (!rule.prefixes.includes(firstChar)) {
        const list = rule.prefixes.length === 1 ? rule.prefixes[0] : rule.prefixes.slice(0, -1).join(', ') + ' or ' + rule.prefixes[rule.prefixes.length - 1];
        return { valid: false, error: `${bankName} account numbers must start with ${list}.` };
      }
    }
    if (/^(\d)\1+$/.test(value)) {
      return { valid: false, error: 'Invalid account number — cannot be all the same digit.' };
    }
    return { valid: true, error: '' };
  };

  // Validates company / trust registration numbers (NOT used for ID-like structures)
  const validateCompanyRegistration = (structure, rawValue) => {
    const value = (rawValue || '').trim();
    if (value === '') return { valid: false, error: '' };
    if (/\s/.test(value)) return { valid: false, error: 'Registration number must not contain spaces' };

    if (structure === 'Trust') {
      if (!/IT/.test(value)) return { valid: false, error: 'Trust registration number must contain the capital letters "IT"' };
      return { valid: true, error: '' };
    }

    const suffix = COMPANY_REG_SUFFIX[structure];
    if (!suffix) return { valid: true, error: '' };

    // Early-warning: the first 4 digits of the registration number represent the year.
    // As soon as we have 4 digits, validate the year so the user is informed before completing.
    const currentYear = new Date().getFullYear();
    const firstSegment = value.split('/')[0];
    if (/^\d{4}/.test(firstSegment)) {
      const earlyYear = parseInt(firstSegment.slice(0, 4), 10);
      if (earlyYear < 1950) return { valid: false, error: `Registration year must be 1950 or later (you entered ${earlyYear})` };
      if (earlyYear > 2040) return { valid: false, error: `Registration year must be 2040 or earlier (you entered ${earlyYear})` };
      if (earlyYear > currentYear) return { valid: false, error: `Registration year cannot be in the future. The current year is ${currentYear} (you entered ${earlyYear})` };
    }

    const match = value.match(/^(\d{4})\/(\d{6})\/(\d{2})$/);
    if (!match) {
      return { valid: false, error: `Format must be YYYY/NNNNNN/${suffix} (e.g. 2023/123456/${suffix})` };
    }
    if (match[3] !== suffix) {
      return { valid: false, error: `${structure} registration numbers must end in /${suffix}` };
    }
    return { valid: true, error: '' };
  };

  useEffect(() => {
    if (!pkg) navigate('/', { replace: true });
    window.scrollTo(0, 0);
  }, [pkg, navigate]);

  // Auto-sync Account Owner with Company Name until the user manually edits it
  useEffect(() => {
    if (!accountOwnerEdited) {
      setFormData(prev => (
        prev.accountOwner === (prev.companyName || '') ? prev : { ...prev, accountOwner: prev.companyName || '' }
      ));
    }
  }, [formData.companyName, accountOwnerEdited]);

  if (!pkg) return null;

  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const validateTaxNumber = (taxNumber) => {
    if (!taxNumber || taxNumber.trim() === '') return { valid: false, error: '' };
    const cleanTax = taxNumber.replace(/\s/g, '');
    if (/[a-zA-Z]/.test(taxNumber)) return { valid: false, error: 'Company Tax Number must not contain letters' };
    if (/[^0-9\s]/.test(taxNumber)) return { valid: false, error: 'Company Tax Number must not contain special characters' };
    if (cleanTax.length !== 10) return { valid: false, error: `Company Tax Number must be exactly 10 digits. Currently ${cleanTax.length} digits.` };
    if (cleanTax[0] !== '9') return { valid: false, error: 'Company Tax Number must start with the digit 9' };
    if (/^(\d)\1{9}$/.test(cleanTax)) return { valid: false, error: 'Invalid Company Tax Number - cannot be all the same digit' };
    return { valid: true, error: '' };
  };

  const validateVatNumber = (vat) => {
    // Optional field — empty is valid
    if (!vat || vat.trim() === '') return { valid: true, error: '' };
    const cleanVat = vat.replace(/\s/g, '');
    if (/[a-zA-Z]/.test(vat)) return { valid: false, error: 'VAT Number must not contain letters' };
    if (/[^0-9\s]/.test(vat)) return { valid: false, error: 'VAT Number must not contain special characters' };
    if (cleanVat.length !== 10) return { valid: false, error: `VAT Number must be exactly 10 digits. Currently ${cleanVat.length} digits.` };
    if (cleanVat[0] !== '4') return { valid: false, error: 'VAT Number must start with the digit 4' };
    if (/^(\d)\1{9}$/.test(cleanVat)) return { valid: false, error: 'Invalid VAT Number - cannot be all the same digit' };
    return { valid: true, error: '' };
  };

  const validSAMobilePrefixes = ['060','061','062','063','064','065','066','067','068','069','071','072','073','074','076','078','079','081','082','083','084'];

  const formatCellphoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)} ${limited.slice(3)}`;
    return `${limited.slice(0, 3)} ${limited.slice(3, 6)} ${limited.slice(6)}`;
  };

  const validateSACellphone = (cellphone) => {
    if (!cellphone || cellphone.trim() === '') return { valid: false, error: '' };
    const digits = cellphone.replace(/\s/g, '');
    if (digits.length !== 10) return { valid: false, error: `Cellphone number must be exactly 10 digits. Currently ${digits.length} digits.` };
    if (!/^\d+$/.test(digits)) return { valid: false, error: 'Cellphone number must contain only numbers' };
    if (digits[0] !== '0') return { valid: false, error: 'Cellphone number must start with 0' };
    if (!validSAMobilePrefixes.includes(digits.slice(0, 3))) return { valid: false, error: 'Invalid cellphone number, please double check' };
    return { valid: true, error: '' };
  };

  const validateLuhnChecksum = (idNumber) => {
    let sum = 0, isSecond = false;
    for (let i = idNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(idNumber[i], 10);
      if (isSecond) { digit *= 2; if (digit > 9) digit = Math.floor(digit / 10) + (digit % 10); }
      sum += digit;
      isSecond = !isSecond;
    }
    return sum % 10 === 0;
  };

  const validateSAIdNumber = (idNumber) => {
    if (!idNumber || idNumber.trim() === '') return { valid: false, error: '', info: null };
    const cleanId = idNumber.replace(/[\s-]/g, '');
    if (!/^\d{13}$/.test(cleanId)) return { valid: false, error: 'ID number must be exactly 13 digits (no letters or special characters)', info: null };
    const month = parseInt(cleanId.substring(2, 4), 10);
    const day = parseInt(cleanId.substring(4, 6), 10);
    const yearNum = parseInt(cleanId.substring(0, 2), 10);
    const citizenshipDigit = cleanId.substring(10, 11);
    if (month < 1 || month > 12) return { valid: false, error: `Invalid month in ID number: ${cleanId.substring(2,4)}. Month must be between 01 and 12`, info: null };
    const currentYear = new Date().getFullYear();
    const fullYear = yearNum <= (currentYear % 100) + 10 ? 2000 + yearNum : 1900 + yearNum;
    const isLeapYear = (fullYear % 4 === 0 && fullYear % 100 !== 0) || (fullYear % 400 === 0);
    const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day < 1 || day > daysInMonth[month - 1]) return { valid: false, error: `Invalid day in ID number: ${cleanId.substring(4,6)}.`, info: null };
    const birthDate = new Date(fullYear, month - 1, day);
    if (birthDate > new Date()) return { valid: false, error: 'Date of birth cannot be in the future', info: null };
    if (!['0','1','2'].includes(citizenshipDigit)) return { valid: false, error: `Invalid citizenship digit: ${citizenshipDigit}.`, info: null };
    if (!validateLuhnChecksum(cleanId)) return { valid: false, error: 'Invalid ID number: Check digit validation failed', info: null };
    const genderNum = parseInt(cleanId.substring(6, 10), 10);
    const gender = genderNum < 5000 ? 'Female' : 'Male';
    const citizenship = citizenshipDigit === '0' ? 'South African Citizen' : citizenshipDigit === '1' ? 'Permanent Resident' : 'Refugee';
    let age = new Date().getFullYear() - birthDate.getFullYear();
    const md = new Date().getMonth() - birthDate.getMonth();
    if (md < 0 || (md === 0 && new Date().getDate() < birthDate.getDate())) age--;
    return { valid: true, error: '', info: { dateOfBirth: birthDate.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }), age, gender, citizenship } };
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === '') return '';
    if (!emailRegex.test(email)) return 'Please enter a valid email address (e.g., name@example.com)';
    return '';
  };

  const validateWorkEmail = (workEmail) => {
    if (!workEmail || workEmail.trim() === '') return '';
    if (!emailRegex.test(workEmail)) return 'Please enter a valid email address (e.g., name@example.com)';
    if (formData.emailAddress && workEmail.toLowerCase().trim() === formData.emailAddress.toLowerCase().trim())
      return 'Work/Alternative email cannot be the same as your personal email address';
    return '';
  };

  const skipTrimFields = ['physicalAddress', 'idNumber', 'taxNumber', 'cellphoneNumber'];

  const handleInputBlur = (field) => {
    if (skipTrimFields.includes(field)) return;
    const currentValue = formData[field];
    if (currentValue && typeof currentValue === 'string') {
      const trimmed = currentValue.trim().replace(/\s+/g, ' ');
      if (trimmed !== currentValue) setFormData(prev => ({ ...prev, [field]: trimmed }));
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'cellphoneNumber') {
      const formatted = formatCellphoneNumber(value);
      setFormData(prev => ({ ...prev, [field]: formatted }));
      if (validateSACellphone(formatted).valid) setFieldErrors(prev => ({ ...prev, cellphoneNumber: '' }));
      return;
    }
    if (field === 'taxNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [field]: digitsOnly }));
      const result = validateTaxNumber(digitsOnly);
      // Show live error so the user is guided as they type
      setFieldErrors(prev => ({ ...prev, taxNumber: result.error }));
      return;
    }
    if (field === 'vatNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [field]: digitsOnly }));
      const result = validateVatNumber(digitsOnly);
      setFieldErrors(prev => ({ ...prev, vatNumber: result.error }));
      return;
    }
    if (field === 'bankName') {
      const branch = BANK_BRANCH_CODES[value] || '';
      setFormData(prev => ({ ...prev, bankName: value, branchCode: branch }));
      if (value) setFieldErrors(prev => ({ ...prev, bankName: '' }));
      // Re-validate the existing account number against the new bank's rule
      const accountValue = formData.accountNumber || '';
      if (accountValue) {
        const result = validateBankAccountNumber(value, accountValue);
        setFieldErrors(prev => ({ ...prev, accountNumber: result.error }));
      }
      return;
    }
    if (field === 'accountOwner') {
      setAccountOwnerEdited(true);
      setFormData(prev => ({ ...prev, accountOwner: value }));
      if (value && value.trim()) setFieldErrors(prev => ({ ...prev, accountOwner: '' }));
      return;
    }
    if (field === 'accountNumber') {
      const rule = BANK_ACCOUNT_RULES[formData.bankName] || GENERIC_ACCOUNT_RULE;
      const cap = Math.max(rule.maxLength, rule.rejectLength || 0);
      const digitsOnly = value.replace(/\D/g, '').slice(0, cap);
      setFormData(prev => ({ ...prev, accountNumber: digitsOnly }));
      const result = validateBankAccountNumber(formData.bankName, digitsOnly);
      setFieldErrors(prev => ({ ...prev, accountNumber: result.error }));
      return;
    }
    if (field === 'businessStructure') {
      const nowIdLike = idLikeStructures.includes(value);
      setFormData(prev => ({ ...prev, [field]: value, companyRegistration: '' }));
      setFieldErrors(prev => ({ ...prev, companyRegistration: '', companyName: '' }));
      setTouchedFields(prev => ({ ...prev, companyRegistration: false, companyName: false }));
      setCompanyRegIdInfo(null);
      // No further processing needed
      void nowIdLike;
      return;
    }
    if (field === 'companyName' && nameValidatedStructures.includes(formData.businessStructure)) {
      setFormData(prev => ({ ...prev, [field]: value }));
      const result = validateCompanyName(formData.businessStructure, value);
      setFieldErrors(prev => ({ ...prev, companyName: result.error }));
      return;
    }
    if (field === 'companyRegistration' && isIdLikeStructure) {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 13);
      setFormData(prev => ({ ...prev, [field]: digitsOnly }));
      const result = validateSAIdNumber(digitsOnly);
      if (digitsOnly.length === 0) {
        setFieldErrors(prev => ({ ...prev, companyRegistration: '' }));
        setCompanyRegIdInfo(null);
      } else if (digitsOnly.length < 13) {
        setFieldErrors(prev => ({ ...prev, companyRegistration: `ID number must be exactly 13 digits. Currently ${digitsOnly.length} digits.` }));
        setCompanyRegIdInfo(null);
      } else if (!result.valid) {
        setFieldErrors(prev => ({ ...prev, companyRegistration: result.error || 'Invalid ID number' }));
        setCompanyRegIdInfo(null);
      } else {
        setFieldErrors(prev => ({ ...prev, companyRegistration: '' }));
        setCompanyRegIdInfo(result.info);
      }
      return;
    }
    if (field === 'companyRegistration' && (isTrustStructure || isCompanyRegStructure)) {
      // Strip spaces; for company structures additionally allow only digits + "/"
      let cleaned = value.replace(/\s/g, '');
      if (isCompanyRegStructure) cleaned = cleaned.replace(/[^0-9/]/g, '').slice(0, 14);
      setFormData(prev => ({ ...prev, [field]: cleaned }));
      const result = validateCompanyRegistration(formData.businessStructure, cleaned);
      setFieldErrors(prev => ({ ...prev, companyRegistration: result.error }));
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    if ((field === 'emailAddress' || field === 'workEmail') && value && emailRegex.test(value))
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'idNumber') {
      const result = validateSAIdNumber(value);
      if (result.valid) { setFieldErrors(prev => ({ ...prev, idNumber: '' })); setIdNumberInfo(result.info); }
      else setIdNumberInfo(null);
    }
  };

  const handleEmailBlur = (field) => {
    const currentValue = formData[field];
    if (currentValue) {
      const trimmed = currentValue.trim().replace(/\s+/g, '');
      if (trimmed !== currentValue) setFormData(prev => ({ ...prev, [field]: trimmed }));
    }
    setTouchedFields(prev => ({ ...prev, [field]: true }));
    const valueToValidate = (currentValue || '').trim().replace(/\s+/g, '');
    setFieldErrors(prev => ({ ...prev, [field]: field === 'workEmail' ? validateWorkEmail(valueToValidate) : validateEmail(valueToValidate) }));
  };

  const handleTaxNumberBlur = () => {
    setTouchedFields(prev => ({ ...prev, taxNumber: true }));
    setFieldErrors(prev => ({ ...prev, taxNumber: validateTaxNumber(formData.taxNumber).error }));
  };

  const handleVatNumberBlur = () => {
    setTouchedFields(prev => ({ ...prev, vatNumber: true }));
    setFieldErrors(prev => ({ ...prev, vatNumber: validateVatNumber(formData.vatNumber).error }));
  };

  const handleCellphoneBlur = () => {
    setTouchedFields(prev => ({ ...prev, cellphoneNumber: true }));
    setFieldErrors(prev => ({ ...prev, cellphoneNumber: validateSACellphone(formData.cellphoneNumber).error }));
  };

  const handleIdNumberBlur = () => {
    setTouchedFields(prev => ({ ...prev, idNumber: true }));
    const result = validateSAIdNumber(formData.idNumber);
    setFieldErrors(prev => ({ ...prev, idNumber: result.error }));
    setIdNumberInfo(result.valid ? result.info : null);
  };

  const handleCompanyRegistrationBlur = () => {
    if (isIdLikeStructure) {
      setTouchedFields(prev => ({ ...prev, companyRegistration: true }));
      const value = formData.companyRegistration || '';
      if (value.length === 0) {
        setFieldErrors(prev => ({ ...prev, companyRegistration: '' }));
        setCompanyRegIdInfo(null);
        return;
      }
      if (value.length < 13) {
        setFieldErrors(prev => ({ ...prev, companyRegistration: `ID number must be exactly 13 digits. Currently ${value.length} digits.` }));
        setCompanyRegIdInfo(null);
        return;
      }
      const result = validateSAIdNumber(value);
      setFieldErrors(prev => ({ ...prev, companyRegistration: result.error }));
      setCompanyRegIdInfo(result.valid ? result.info : null);
      return;
    }
    if (isTrustStructure || isCompanyRegStructure) {
      setTouchedFields(prev => ({ ...prev, companyRegistration: true }));
      const result = validateCompanyRegistration(formData.businessStructure, formData.companyRegistration);
      setFieldErrors(prev => ({ ...prev, companyRegistration: result.error }));
      return;
    }
    handleInputBlur('companyRegistration');
  };

  const requiredFields = [
    { key: 'firstName', label: 'First Name' },
    { key: 'surname', label: 'Surname' },
    { key: 'idNumber', label: 'ID Number' },
    { key: 'taxNumber', label: 'Company Tax Number' },
    { key: 'emailAddress', label: 'Email Address' },
    { key: 'cellphoneNumber', label: 'Cellphone Number' },
    { key: 'physicalAddress', label: 'Physical Address' }
  ];

  const validateForm = () => {
    if (!clientType) { setErrorModal({ open: true, message: 'Please select whether you are a New Client or Existing Client.' }); return false; }
    if (!formData.businessStructure) { setErrorModal({ open: true, message: 'Please select your Business Legal Structure.' }); return false; }
    const sLabels = getStructureLabels(formData.businessStructure);
    if (!formData.companyName || formData.companyName.trim() === '') { setErrorModal({ open: true, message: `Please fill in the required field: ${sLabels.name}` }); return false; }
    if (isNameValidatedStructure) {
      const nameResult = validateCompanyName(formData.businessStructure, formData.companyName);
      if (!nameResult.valid) {
        const errMsg = nameResult.error || `Invalid ${sLabels.name}`;
        setFieldErrors(prev => ({ ...prev, companyName: errMsg }));
        setTouchedFields(prev => ({ ...prev, companyName: true }));
        setErrorModal({ open: true, message: `${sLabels.name}: ${errMsg}` });
        return false;
      }
    }
    if (!formData.companyRegistration || formData.companyRegistration.trim() === '') { setErrorModal({ open: true, message: `Please fill in the required field: ${sLabels.reg}` }); return false; }
    if (isIdLikeStructure) {
      const regResult = validateSAIdNumber(formData.companyRegistration);
      if (!regResult.valid) {
        const errMsg = regResult.error || 'Invalid ID number';
        setFieldErrors(prev => ({ ...prev, companyRegistration: errMsg }));
        setTouchedFields(prev => ({ ...prev, companyRegistration: true }));
        setErrorModal({ open: true, message: `${sLabels.reg}: ${errMsg}` });
        return false;
      }
    } else if (isTrustStructure || isCompanyRegStructure) {
      const regResult = validateCompanyRegistration(formData.businessStructure, formData.companyRegistration);
      if (!regResult.valid) {
        const errMsg = regResult.error || `Invalid ${sLabels.reg}`;
        setFieldErrors(prev => ({ ...prev, companyRegistration: errMsg }));
        setTouchedFields(prev => ({ ...prev, companyRegistration: true }));
        setErrorModal({ open: true, message: `${sLabels.reg}: ${errMsg}` });
        return false;
      }
    }
    for (const field of requiredFields) {
      if (!formData[field.key] || formData[field.key].trim() === '') {
        setErrorModal({ open: true, message: `Please fill in the required field: ${field.label}` }); return false;
      }
    }
    const idResult = validateSAIdNumber(formData.idNumber);
    if (!idResult.valid && idResult.error) {
      setFieldErrors(prev => ({ ...prev, idNumber: idResult.error }));
      setTouchedFields(prev => ({ ...prev, idNumber: true }));
      setErrorModal({ open: true, message: 'ID Number: ' + idResult.error }); return false;
    }
    const taxResult = validateTaxNumber(formData.taxNumber);
    if (!taxResult.valid && taxResult.error) {
      setFieldErrors(prev => ({ ...prev, taxNumber: taxResult.error }));
      setTouchedFields(prev => ({ ...prev, taxNumber: true }));
      setErrorModal({ open: true, message: 'Company Tax Number: ' + taxResult.error }); return false;
    }
    // VAT Number is optional, but if provided it must be valid
    if (formData.vatNumber && formData.vatNumber.trim()) {
      const vatResult = validateVatNumber(formData.vatNumber);
      if (!vatResult.valid && vatResult.error) {
        setFieldErrors(prev => ({ ...prev, vatNumber: vatResult.error }));
        setTouchedFields(prev => ({ ...prev, vatNumber: true }));
        setErrorModal({ open: true, message: 'Company VAT Number: ' + vatResult.error }); return false;
      }
    }

    // Banking details — Bank Name, Account Owner, Account Number all required (Branch Code is system-populated)
    if (!formData.bankName || formData.bankName.trim() === '') {
      setFieldErrors(prev => ({ ...prev, bankName: 'Please select your bank' }));
      setTouchedFields(prev => ({ ...prev, bankName: true }));
      setErrorModal({ open: true, message: 'Please fill in the required field: Bank Name' }); return false;
    }
    if (!formData.accountOwner || formData.accountOwner.trim() === '') {
      setFieldErrors(prev => ({ ...prev, accountOwner: 'Account Owner is required' }));
      setTouchedFields(prev => ({ ...prev, accountOwner: true }));
      setErrorModal({ open: true, message: 'Please fill in the required field: Account Owner' }); return false;
    }
    if (!formData.accountNumber || formData.accountNumber.trim() === '') {
      setFieldErrors(prev => ({ ...prev, accountNumber: 'Account Number is required' }));
      setTouchedFields(prev => ({ ...prev, accountNumber: true }));
      setErrorModal({ open: true, message: 'Please fill in the required field: Account Number' }); return false;
    }
    const acctResult = validateBankAccountNumber(formData.bankName, formData.accountNumber);
    if (!acctResult.valid && acctResult.error) {
      setFieldErrors(prev => ({ ...prev, accountNumber: acctResult.error }));
      setTouchedFields(prev => ({ ...prev, accountNumber: true }));
      setErrorModal({ open: true, message: 'Account Number: ' + acctResult.error }); return false;
    }
    const cellResult = validateSACellphone(formData.cellphoneNumber);
    if (!cellResult.valid && cellResult.error) {
      setFieldErrors(prev => ({ ...prev, cellphoneNumber: cellResult.error }));
      setTouchedFields(prev => ({ ...prev, cellphoneNumber: true }));
      setErrorModal({ open: true, message: 'Cellphone Number: ' + cellResult.error }); return false;
    }
    const emailError = validateEmail(formData.emailAddress);
    if (emailError) {
      setFieldErrors(prev => ({ ...prev, emailAddress: emailError }));
      setTouchedFields(prev => ({ ...prev, emailAddress: true }));
      setErrorModal({ open: true, message: 'Email Address: ' + emailError }); return false;
    }
    if (formData.workEmail && formData.workEmail.trim()) {
      const workEmailError = validateWorkEmail(formData.workEmail);
      if (workEmailError) {
        setFieldErrors(prev => ({ ...prev, workEmail: workEmailError }));
        setTouchedFields(prev => ({ ...prev, workEmail: true }));
        setErrorModal({ open: true, message: 'Work/Alternative Email Address: ' + workEmailError }); return false;
      }
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    // Merge with any existing data (e.g. signature/location captured on later pages)
    // so navigating back-and-forth never wipes data from other steps.
    updateClientData({ ...(clientData || {}), clientType, packageSlug, ...formData });
    navigate(getStepPath(packageSlug, 'poa'));
  };

  const isPhysicalAddressValid = () => {
    if (!formData.physicalAddress) return false;
    return formData.physicalAddress.split('\n').filter(l => l.trim().length > 0).length >= 3;
  };

  const isFormValid = () =>
    clientType && formData.businessStructure && formData.companyName && formData.companyRegistration &&
    formData.firstName && formData.surname && formData.idNumber &&
    formData.taxNumber && formData.emailAddress && formData.cellphoneNumber && isPhysicalAddressValid() &&
    formData.bankName && formData.accountOwner && formData.accountOwner.trim() && formData.accountNumber;

  const levelStyles = getLevelStyles(pkg.colorKey);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <main className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-8 sm:pb-12 bg-white">
        {/* Logo */}
        <div className="mb-6 sm:mb-8 flex flex-col items-center gap-4">
          <img src={LOGO_URL} alt="TCA Auditors Logo" className="h-24 sm:h-32 w-auto object-contain select-none" draggable="false" />
          <img src={BANNER_URL} alt="Tax & Auditing Consultants - For Businesses & Individuals" className="h-[4.5rem] sm:h-24 w-auto object-contain" />
        </div>

        {/* Heading */}
        <div className="mb-4 pb-2">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            ACCOUNTING RETAINER AGREEMENT
          </h1>
        </div>

        {/* Top back button — matches Comparison Table style */}
        <div className="w-full max-w-2xl flex justify-start mb-6">
          <Button
            onClick={() => navigate(getStepPath(packageSlug, 'intro'))}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-intro-top"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Introduction
          </Button>
        </div>

        <Card className="w-full max-w-2xl border-0 shadow-card">
          <CardContent className="p-6 sm:p-10">
            {/* Package badge */}
            <div className="flex justify-center mb-6">
              <span className="text-sm font-bold px-4 py-1.5 rounded-full" style={levelStyles} data-testid="package-badge">
                {pkg.fullName}
              </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Client Type */}
              <div className="space-y-4">
                <div className="pb-2">
                  <Label className="text-lg font-semibold text-tca-blue inline-block underline-tca-blue">
                    Please select your client status:
                  </Label>
                </div>
                <RadioGroup value={clientType} onValueChange={setClientType} className="flex flex-col sm:flex-row gap-12 sm:gap-24 pt-3">
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="new" id="new-client" className={`h-5 w-5 border-2 ${clientType === 'new' ? 'radio-orange' : 'border-muted-foreground'}`} />
                    <Label htmlFor="new-client" className={`text-base font-medium cursor-pointer transition-all duration-300 ${clientType === 'new' ? 'gradient-bg-orange font-semibold' : 'text-foreground'}`}>
                      New Client
                    </Label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <RadioGroupItem value="existing" id="existing-client" className={`h-5 w-5 border-2 ${clientType === 'existing' ? 'radio-green' : 'border-muted-foreground'}`} />
                    <Label htmlFor="existing-client" className={`text-base font-medium cursor-pointer transition-all duration-300 ${clientType === 'existing' ? 'gradient-bg-green font-semibold' : 'text-foreground'}`}>
                      Existing Client (Renewal of Agreement)
                    </Label>
                  </div>
                </RadioGroup>
                {clientType === 'existing' && (
                  <p className="text-sm italic mt-2" style={{ color: 'hsl(142, 76%, 36%)' }}>
                    <strong>Welcome back!</strong> As a valued client, exclusive discounted prices await you!
                    <br />
                    Complete the renewal of your Accounting Retainer Agreement to secure discounted renewal prices for our loyal clients. The discounted retainer prices will be presented in the screens that follow.
                  </p>
                )}
              </div>

              {clientType && (
                <div className="space-y-6 animate-fade-in">
                  <div className="border-t border-border" />

                  {clientType === 'new' && (
                    <div className="space-y-2">
                      <Label className="text-sm font-bold text-foreground">How did you hear about us?</Label>
                      <Select value={formData.referralSource} onValueChange={(value) => handleInputChange('referralSource', value)}>
                        <SelectTrigger className="h-11 px-4 bg-background border-input hover:border-muted-foreground/50 focus:ring-2 focus:ring-accent focus:ring-offset-0">
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          {REFERRAL_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Business Legal Structure */}
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-foreground">
                      Business Legal Structure<span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.businessStructure}
                      onValueChange={(v) => handleInputChange('businessStructure', v)}
                      data-testid="select-business-structure"
                    >
                      <SelectTrigger className="h-11 px-4 bg-background border-input hover:border-muted-foreground/50 focus:ring-2 focus:ring-accent focus:ring-offset-0">
                        <SelectValue placeholder="Select your business structure" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUSINESS_STRUCTURES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      For interesting information on the various structures,{' '}
                      <a
                        href="https://tcauditors.co.za/structures"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-tca-blue underline hover:opacity-75"
                      >
                        click here
                      </a>.
                    </p>
                  </div>

                  {/* Company / Business / Trust name & registration — labels change per structure. Visible as soon as a client type is chosen, but disabled until a Business Legal Structure is selected. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <FormField
                      label={formData.businessStructure ? getStructureLabels(formData.businessStructure).name : 'Company Name'}
                      required
                      value={formData.companyName}
                      onChange={(v) => handleInputChange('companyName', v)}
                      onBlur={() => {
                        handleInputBlur('companyName');
                        if (isNameValidatedStructure) {
                          setTouchedFields(prev => ({ ...prev, companyName: true }));
                          const result = validateCompanyName(formData.businessStructure, formData.companyName);
                          setFieldErrors(prev => ({ ...prev, companyName: result.error }));
                        }
                      }}
                      placeholder={formData.businessStructure ? getStructureLabels(formData.businessStructure).namePlaceholder : 'Select Business Legal Structure first'}
                      disabled={!formData.businessStructure}
                      error={isNameValidatedStructure ? fieldErrors.companyName : ''}
                      showError={isNameValidatedStructure && !!fieldErrors.companyName}
                      data-testid="input-company-name"
                    />
                    <div className="space-y-2">
                      <FormField
                        label={formData.businessStructure ? getStructureLabels(formData.businessStructure).reg : 'Company Registration #'}
                        required
                        value={formData.companyRegistration}
                        onChange={(v) => handleInputChange('companyRegistration', v)}
                        onBlur={handleCompanyRegistrationBlur}
                        placeholder={formData.businessStructure ? getStructureLabels(formData.businessStructure).regPlaceholder : 'Select Business Legal Structure first'}
                        disabled={!formData.businessStructure}
                        digitsOnly={isIdLikeStructure}
                        error={(isIdLikeStructure || isTrustStructure || isCompanyRegStructure) ? fieldErrors.companyRegistration : ''}
                        showError={(isIdLikeStructure || isTrustStructure || isCompanyRegStructure) && !!fieldErrors.companyRegistration}
                        data-testid="input-company-registration"
                      />
                      {isIdLikeStructure && companyRegIdInfo && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                          <p className="font-semibold text-green-800">Valid South African ID Number</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <FormField label="First Name" required value={formData.firstName} onChange={(v) => handleInputChange('firstName', v)} onBlur={() => handleInputBlur('firstName')} placeholder="Enter your first name" disabled={!formData.businessStructure} />
                    <FormField label="Second Name" value={formData.secondName} onChange={(v) => handleInputChange('secondName', v)} onBlur={() => handleInputBlur('secondName')} placeholder="Enter your second name" disabled={!formData.businessStructure} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <FormField label="Middle Name" value={formData.middleName} onChange={(v) => handleInputChange('middleName', v)} onBlur={() => handleInputBlur('middleName')} placeholder="Enter your middle name" disabled={!formData.businessStructure} />
                    <FormField label="Surname" required value={formData.surname} onChange={(v) => handleInputChange('surname', v)} onBlur={() => handleInputBlur('surname')} placeholder="Enter your surname" disabled={!formData.businessStructure} />
                  </div>

                  <div className="space-y-2">
                    <FormField label="ID Number" required value={formData.idNumber} onChange={(v) => handleInputChange('idNumber', v)} onBlur={handleIdNumberBlur} placeholder="Enter your 13-digit SA ID number" error={fieldErrors.idNumber} showError={touchedFields.idNumber} digitsOnly={true} disabled={!formData.businessStructure} />
                    {idNumberInfo && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                        <p className="font-semibold text-green-800">Valid South African ID Number</p>
                      </div>
                    )}
                  </div>

                  <FormField label="Company Tax Number" required value={formData.taxNumber} onChange={(v) => handleInputChange('taxNumber', v)} onBlur={handleTaxNumberBlur} placeholder="Enter the 10-digit company tax number (starts with 9)" error={fieldErrors.taxNumber} showError={!!fieldErrors.taxNumber || touchedFields.taxNumber} disabled={!formData.businessStructure} digitsOnly={true} />

                  <FormField
                    label="Company VAT Number (if applicable)"
                    value={formData.vatNumber}
                    onChange={(v) => handleInputChange('vatNumber', v)}
                    onBlur={handleVatNumberBlur}
                    placeholder="Enter the 10-digit VAT number (starts with 4) — optional"
                    error={fieldErrors.vatNumber}
                    showError={!!fieldErrors.vatNumber || touchedFields.vatNumber}
                    disabled={!formData.businessStructure}
                    digitsOnly={true}
                    data-testid="input-vat-number"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <FormField label="Email Address" required type="email" value={formData.emailAddress} onChange={(v) => handleInputChange('emailAddress', v)} onBlur={() => handleEmailBlur('emailAddress')} placeholder="your.email@example.com" error={fieldErrors.emailAddress} showError={touchedFields.emailAddress} disabled={!formData.businessStructure} />
                    <FormField label="Work/Alternative Email Address" type="email" value={formData.workEmail} onChange={(v) => handleInputChange('workEmail', v)} onBlur={() => handleEmailBlur('workEmail')} placeholder="work.email@company.com (optional)" error={fieldErrors.workEmail} showError={touchedFields.workEmail} disabled={!formData.businessStructure} />
                  </div>

                  <FormField label="Cellphone Number" required type="tel" value={formData.cellphoneNumber} onChange={(v) => handleInputChange('cellphoneNumber', v)} onBlur={handleCellphoneBlur} placeholder="0XX XXX XXXX" error={fieldErrors.cellphoneNumber} showError={touchedFields.cellphoneNumber} disabled={!formData.businessStructure} />

                  <div className="space-y-2 relative">
                    {showAddressTip && formData.physicalAddress && (
                      <div className="absolute left-0 right-0 -top-2 transform -translate-y-full z-10 bg-blue-50 border border-blue-200 rounded-lg p-3 shadow-lg">
                        <div className="flex items-start gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="text-xs text-blue-800">
                            <p className="font-semibold mb-1">Address line guidance:</p>
                            <p>Line 1: Unit number (e.g. Unit 01)</p>
                            <p>Line 2: Estate/Complex name (e.g. Steyn City Estate)</p>
                            <p>Line 3: Street address (e.g. 33 William Nicol Road)</p>
                            <p>Line 4: Suburb/City (e.g. Sandton)</p>
                            <p>Line 5: Postal code (e.g. 2196)</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <Label className={`text-sm font-bold ${!formData.businessStructure ? 'text-muted-foreground/60' : 'text-foreground'}`}>
                      Physical Address (as at today)<span className={!formData.businessStructure ? 'text-muted-foreground/60' : 'text-red-500'}>*</span>
                    </Label>
                    <textarea
                      value={formData.physicalAddress}
                      onChange={(e) => {
                        const value = e.target.value;
                        const lines = value.split('\n');
                        handleInputChange('physicalAddress', lines.length <= 5 ? value : lines.slice(0, 5).join('\n'));
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && formData.physicalAddress.split('\n').length >= 5) e.preventDefault(); }}
                      onFocus={() => setShowAddressTip(true)}
                      onBlur={() => setShowAddressTip(false)}
                      disabled={!formData.businessStructure}
                      placeholder={!formData.businessStructure ? 'Select Business Legal Structure first' : "Physical address line 01 (e.g. Unit 01)\nPhysical address line 02 (e.g. Steyn City Estate)\nPhysical address line 03 (e.g. 33 William Nicol Road)\nPhysical address line 04 (e.g. Sandton)\nPhysical address line 05 (e.g. 2196)"}
                      rows={5}
                      className={`w-full px-4 py-3 rounded-md border text-sm resize-none transition-colors duration-200 ${
                        !formData.businessStructure
                          ? 'bg-muted/60 text-muted-foreground/70 placeholder:text-muted-foreground/50 border-muted cursor-not-allowed'
                          : 'bg-background border-input hover:border-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-0'
                      }`}
                    />
                  </div>

                  {/* Banking details section */}
                  <div className="border-t border-border pt-6 space-y-6">
                    <div className="pb-2">
                      <Label className="text-lg font-semibold text-tca-blue inline-block underline-tca-blue">
                        Banking Details
                      </Label>
                    </div>

                    {/* Bank Name + Branch Code */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label className={`text-sm font-bold flex items-center gap-0.5 ${!formData.businessStructure ? 'text-muted-foreground/60' : 'text-foreground'}`}>
                          Bank Name<span className={!formData.businessStructure ? 'text-muted-foreground/60 ml-0.5' : 'text-destructive ml-0.5'}>*</span>
                        </Label>
                        <Select
                          value={formData.bankName}
                          onValueChange={(v) => handleInputChange('bankName', v)}
                          disabled={!formData.businessStructure}
                          data-testid="select-bank-name"
                        >
                          <SelectTrigger
                            className={`h-11 px-4 transition-colors duration-200 ${
                              !formData.businessStructure
                                ? 'bg-muted/60 text-muted-foreground/70 border-muted cursor-not-allowed'
                                : fieldErrors.bankName && touchedFields.bankName
                                  ? 'border-2 border-destructive bg-red-50 focus:ring-destructive'
                                  : 'bg-background border-input hover:border-muted-foreground/50 focus:ring-2 focus:ring-accent focus:ring-offset-0'
                            }`}
                          >
                            <SelectValue placeholder={!formData.businessStructure ? 'Select Business Legal Structure first' : 'Select your bank'} />
                          </SelectTrigger>
                          <SelectContent>
                            {BANKS.map((b) => (
                              <SelectItem key={b} value={b}>{b}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {fieldErrors.bankName && touchedFields.bankName && (
                          <p className="text-sm text-destructive">{fieldErrors.bankName}</p>
                        )}
                      </div>

                      <FormField
                        label="Branch Code"
                        value={formData.branchCode}
                        onChange={() => { /* read-only — auto-populated from selected bank */ }}
                        placeholder={formData.bankName ? '' : 'Auto-populated when bank is selected'}
                        disabled={true}
                        data-testid="input-branch-code"
                      />
                    </div>

                    {/* Account Owner */}
                    <FormField
                      label="Account Owner"
                      required
                      value={formData.accountOwner}
                      onChange={(v) => handleInputChange('accountOwner', v)}
                      onBlur={() => {
                        handleInputBlur('accountOwner');
                        setTouchedFields(prev => ({ ...prev, accountOwner: true }));
                      }}
                      placeholder={!formData.businessStructure ? 'Select Business Legal Structure first' : 'Pre-populated from Company Name (editable)'}
                      disabled={!formData.businessStructure}
                      error={fieldErrors.accountOwner}
                      showError={!!fieldErrors.accountOwner && touchedFields.accountOwner}
                      data-testid="input-account-owner"
                    />

                    {/* Account Number */}
                    <div className="space-y-2">
                      <FormField
                        label="Account Number"
                        required
                        value={formData.accountNumber}
                        onChange={(v) => handleInputChange('accountNumber', v)}
                        onBlur={() => setTouchedFields(prev => ({ ...prev, accountNumber: true }))}
                        placeholder={!formData.businessStructure ? 'Select Business Legal Structure first' : (formData.bankName ? `Enter your bank account number (${(BANK_ACCOUNT_RULES[formData.bankName] || GENERIC_ACCOUNT_RULE).description})` : 'Enter your bank account number (digits only)')}
                        disabled={!formData.businessStructure}
                        digitsOnly={true}
                        error={fieldErrors.accountNumber}
                        showError={!!fieldErrors.accountNumber && touchedFields.accountNumber}
                        data-testid="input-account-number"
                      />
                      {formData.bankName && BANK_ACCOUNT_RULES[formData.bankName]?.note && (
                        <p className="text-xs text-muted-foreground italic">
                          <span className="font-semibold not-italic">Tip:</span> {BANK_ACCOUNT_RULES[formData.bankName].note}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="pt-4 flex flex-col sm:flex-row gap-3 items-center">
                    <Button
                      type="submit"
                      disabled={!isFormValid()}
                      data-testid="btn-next-page"
                      className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Go to the Next Page &rarr;
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Bottom back button */}
        <div className="w-full max-w-2xl flex justify-start mt-4 mb-6">
          <Button
            onClick={() => navigate(getStepPath(packageSlug, 'intro'))}
            className="bg-accent hover:bg-accent/90 text-white font-semibold py-2 px-6 h-9 text-sm rounded-lg shadow-soft hover:shadow-card transition-all duration-200 flex items-center gap-2"
            data-testid="back-to-intro-bottom"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Introduction
          </Button>
        </div>
      </main>

      <div className="relative">
        <PackageStepIndicator pkg={pkg} currentStepKey="details" />
        <img src={TCA_SHORT_LOGO} alt="TCA Logo" className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80" />
      </div>

      <Dialog open={errorModal.open} onOpenChange={(open) => setErrorModal({ ...errorModal, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Required Field Missing
            </DialogTitle>
            <DialogDescription className="text-base pt-2">{errorModal.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setErrorModal({ open: false, message: '' })} className="bg-accent hover:bg-accent/90 text-accent-foreground">
              OK, I'll fix it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PersonalDetails;
